import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import styled from 'styled-components';
import {ArrowLeft, ChevronRight, RefreshCw} from 'lucide-react';
import {apiErrorMessage} from '../api/abfApi';
import {StatusChip} from '../components/StatusChip';
import {ViewportToast, type ActionFeedbackState} from '../components/ActionFeedback';
import {Button, INK} from '../onboarding/review/reviewStyles';
import {Note, SmallButton, Surface} from '../onboarding/review/contractReviewStyles';
import {ABF_FORWARD_FLOW_PATH, ABF_HOME_PATH, forwardFlowProgramPath} from '../routes';
import {ffApi, type ProgramDetail} from './forwardFlowApi';
import {overviewApi, type ProgramAction, type ProgramCard, type ProgramOverview, type ProgramWorkspace} from './forwardFlowOverviewApi';
import {ForwardFlowApprovalsDock} from './ForwardFlowCheckerBar';
import {ForwardFlowOverviewWorkspace} from './ForwardFlowOverviewWorkspace';
import {ForwardFlowAgreementWorkspace} from './ForwardFlowAgreementWorkspace';
import {ForwardFlowOffersWorkspace} from './ForwardFlowOffersWorkspace';
import {ForwardFlowAssetsWorkspace} from './ForwardFlowAssetsWorkspace';
import {ForwardFlowCollectionsWorkspace} from './ForwardFlowCollectionsWorkspace';
import {ForwardFlowMonitoringWorkspace} from './ForwardFlowMonitoringWorkspace';
import {ForwardFlowRemediesPanel} from './ForwardFlowRemediesPanel';
import {ForwardFlowReportingWorkspace} from './ForwardFlowReportingWorkspace';
import {ForwardFlowAuthoritiesWorkbench} from './ForwardFlowAuthoritiesWorkbench';
import {ForwardFlowIdentityAliasPanel} from './ForwardFlowIdentityAliasPanel';
import {ForwardFlowGovernancePanel} from './ForwardFlowGovernancePanel';
import {FeedbackProvider, Fields, Grid, Scroller, Section, useViewer} from './ForwardFlowShared';
import {activityWords, count, day, moment, money, word} from './forwardFlowWords';

const TABS: Array<[ProgramWorkspace, string]> = [['overview', 'Overview'], ['agreement', 'Agreement'], ['offers', 'Offers'],
  ['assets', 'Assets'], ['collections', 'Collections'], ['monitoring', 'Monitoring'], ['reporting', 'Reporting'],
  ['controls', 'Controls'], ['activity', 'Activity']];
/** Links saved before the offer steps were brought onto one tab still resolve. */
const LEGACY: Record<string, ProgramWorkspace> = {intake: 'offers', decisions: 'offers', purchase: 'offers'};

const ContextHeader = styled.header`
  position: sticky; top: 101px; z-index: 40; overflow: hidden; margin-bottom: 4px;
  border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px;
  background: radial-gradient(circle at top right, rgba(14, 116, 144, 0.18), transparent 38%), rgba(5, 12, 24, 0.97);
  box-shadow: 0 18px 55px rgba(2, 6, 23, 0.42); backdrop-filter: blur(16px);
  .top { display: grid; grid-template-columns: minmax(19rem, 1.2fr) minmax(32rem, 2.8fr); gap: 1rem; padding: 1rem; }
  .back { display: inline-flex; align-items: center; gap: 0.35rem; color: #7dd3fc; text-decoration: none; font-size: 0.7rem; font-weight: 800; }
  h1 { margin: 0.55rem 0 0.2rem; color: #f8fafc; font-size: clamp(1.25rem, 2vw, 1.8rem); line-height: 1.15; }
  .meta { color: #94a3b8; font-size: 0.75rem; line-height: 1.5; }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
  .facts { display: grid; grid-template-columns: repeat(4, minmax(8.5rem, 1fr)); gap: 0.55rem; }
  .fact { min-width: 0; padding: 0.65rem 0.7rem; border: 1px solid rgba(148, 163, 184, 0.14); border-radius: 10px; background: rgba(15, 23, 42, 0.62); }
  .fact span { display: block; margin-bottom: 0.28rem; color: #8ea0b8; font-size: 0.58rem; font-weight: 850; letter-spacing: 0.08em; text-transform: uppercase; }
  .fact strong { display: block; overflow: hidden; color: #f8fafc; font-size: 0.84rem; font-weight: 850; text-overflow: ellipsis; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .fact strong[data-tone='amber'] { color: #fcd34d; } .fact strong[data-tone='emerald'] { color: #6ee7b7; }
  .fact small { display: block; overflow: hidden; margin-top: 0.25rem; color: #94a3b8; font-size: 0.62rem; text-overflow: ellipsis; white-space: nowrap; }
  nav { display: flex; gap: 0.15rem; overflow-x: auto; padding: 0 0.75rem; border-top: 1px solid rgba(148, 163, 184, 0.13); }
  nav button { flex: 0 0 auto; padding: 0.75rem 0.68rem; border: 0; border-bottom: 2px solid transparent; background: transparent;
    color: #94a3b8; cursor: pointer; font-size: 0.68rem; font-weight: 800; }
  nav button[aria-selected='true'] { border-bottom-color: #38bdf8; color: #e0f2fe; }
  nav button i { font-style: normal; margin-left: 5px; padding: 1px 6px; border-radius: 999px; background: rgba(251, 191, 36, 0.18); color: #fcd34d; font-size: 0.6rem; }
  @media (max-width: 1050px) { .top { grid-template-columns: 1fr; } }
  @media (max-width: 780px) { .facts { grid-template-columns: repeat(2, minmax(0, 1fr)); } position: static; }
`;
/* The Copilot's button sits at the bottom right; decision bars leave it room so their action stays reachable. */
const Body = styled.div`margin-top: 16px; padding-bottom: 72px; footer { padding-right: 170px; }`;
const Cards = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 14px; margin: 16px 0 24px;
  a { display: flex; flex-direction: column; gap: 12px; padding: 18px; border: 1px solid ${INK.line}; border-radius: 12px; background: ${INK.panel};
    color: ${INK.body}; text-decoration: none; }
  a:hover, a:focus-visible { border-color: ${INK.accent}; background: ${INK.raised}; }
  .top { display: flex; justify-content: space-between; gap: 8px; align-items: flex-start; }
  .top strong { display: block; font-size: 15px; color: ${INK.strong}; } .top small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 3px; }
  .figures { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
  .figures span { display: block; color: ${INK.faint}; font-size: 10px; font-weight: 750; letter-spacing: .06em; text-transform: uppercase; }
  .figures b { display: block; margin-top: 3px; font-size: 14px; font-variant-numeric: tabular-nums; color: ${INK.strong}; }
  .next { padding-top: 12px; border-top: 1px solid ${INK.line}; font-size: 13px; }
  .next em { display: block; font-style: normal; color: ${INK.faint}; font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; margin-bottom: 3px; }
  .next small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 2px; }
`;
const PageHead = styled.div`
  display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-end; gap: 16px; margin: 6px 0 4px;
  h1 { margin: 6px 0 4px; } p { color: ${INK.muted}; font-size: 14px; max-width: 72ch; }
  .back { display: inline-flex; align-items: center; gap: 6px; color: ${INK.accentSoft}; text-decoration: none; font-size: 13px; }
`;

const programRoute = (programId: number, action: ProgramAction) =>
  `${forwardFlowProgramPath(programId)}?workspace=${action.workspace}${action.query ? `&${action.query}` : ''}`;

// --- Directory -------------------------------------------------------------------------------

export function ForwardFlowDirectoryPage({embedded}: {embedded?: boolean} = {}) {
  const navigate = useNavigate();
  const viewer = useViewer();
  const [programs, setPrograms] = useState<ProgramCard[] | null>(null);
  const [portfolios, setPortfolios] = useState<Array<{id: number; portfolio_name: string}>>([]);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(''); const [name, setName] = useState(''); const [portfolio, setPortfolio] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setPrograms(await overviewApi.directory()); }
    catch (e) { setPrograms([]); setFeedback({tone: 'error', message: apiErrorMessage(e, 'The programs could not be loaded.')}); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (creating && !portfolios.length) ffApi.portfolios().then(setPortfolios).catch(() => setPortfolios([])); }, [creating, portfolios.length]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true);
    try {
      const result = await ffApi.createProgram(code.trim(), name.trim(), Number(portfolio));
      navigate(`${forwardFlowProgramPath(result.programId)}?workspace=agreement`);
    } catch (e) { setFeedback({tone: 'error', message: apiErrorMessage(e, 'The program could not be created.')}); }
    finally { setBusy(false); }
  };

  return <Surface>
    <PageHead>
      <div>{!embedded && <Link className="back" to={ABF_HOME_PATH}><ArrowLeft size={14} /> ABF home</Link>}
        <h1>Forward-flow programs</h1>
        <p>Each program buys loans from one seller under a purchase agreement: offers are received and decided, the loans are booked to the
          official record, and they are serviced until they pay off.</p></div>
      <div style={{display: 'flex', gap: 9}}>
        <SmallButton type="button" onClick={() => void load()} disabled={loading}><RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}</SmallButton>
        {viewer.maker && !creating && <Button $variant="primary" type="button" onClick={() => setCreating(true)}>+ New program</Button>}
      </div>
    </PageHead>

    {creating && <Section as="form" onSubmit={create} style={{marginTop: 16}}>
      <header><div><h2>New program</h2><p>This names the program and the portfolio that will own the loans. Nothing can be bought until its agreement is in force.</p></div>
        <SmallButton type="button" onClick={() => setCreating(false)}>Cancel</SmallButton></header>
      <Fields>
        <label>Program name<input required maxLength={255} placeholder="e.g. Granite Creek consumer loans" value={name} onChange={e => setName(e.target.value)} /></label>
        <label>Short code<input required maxLength={80} placeholder="e.g. GRANITE-2026" value={code} onChange={e => setCode(e.target.value)} />
          <small>Shown in lists and reports.</small></label>
        <label>Portfolio that owns the loans<select required value={portfolio} onChange={e => setPortfolio(e.target.value)}>
          <option value="">Choose a portfolio…</option>{portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolio_name}</option>)}</select></label>
      </Fields>
      <Button $variant="primary" type="submit" disabled={busy || !portfolio || !code.trim() || !name.trim()}>
        {busy ? 'Creating…' : 'Create and enter the agreement'} <ChevronRight size={14} /></Button>
    </Section>}

    {programs === null ? <Note role="status" style={{marginTop: 16}}>Loading programs…</Note>
      : programs.length === 0 ? <Note style={{marginTop: 16}}><strong>No forward-flow program yet.</strong>
        <p>{viewer.maker ? 'Create one to start: name it, choose the portfolio that will own the loans, then enter its purchase agreement.'
          : 'An operations or portfolio user creates the first program.'}</p></Note>
      : <Cards>{programs.map(p => <Link key={p.id} to={forwardFlowProgramPath(p.id)}>
        <div className="top"><div><strong>{p.programName}</strong><small>{p.programCode} · {p.portfolioName}</small></div>
          <StatusChip tone={word('program', p.status).tone} label={p.statusLabel} /></div>
        {p.facts && <div className="figures">
          <div><span>Loans owned</span><b>{Number(p.facts.owned.loans).toLocaleString('en-US')}</b></div>
          <div><span>Balance owned</span><b>{money(p.facts.owned.upb, 0)}</b></div>
          <div><span>Limit remaining</span><b>{p.facts.capacity.known ? money(p.facts.capacity.remaining, 0) : p.facts.capacity.closed ? 'Closed' : 'Not yet known'}</b></div>
        </div>}
        <div className="next"><em>Next</em>{p.nextAction.label}
          <small>{(p.approvals ?? 0) > 0 ? `${count(p.approvals, 'item')} waiting for a checker` : p.nextAction.whyNot ?? p.nextAction.reason}</small></div>
      </Link>)}</Cards>}

    <ForwardFlowApprovalsDock />
    <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
  </Surface>;
}

// --- Program ---------------------------------------------------------------------------------

export function ForwardFlowProgramPage() {
  const programId = Number(useParams().programId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState<ProgramOverview | null>(null);
  const [detail, setDetail] = useState<ProgramDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<ActionFeedbackState | null>(null);
  const [problem, setProblem] = useState('');

  const asked = searchParams.get('workspace') ?? 'overview';
  const tab: ProgramWorkspace = TABS.some(([key]) => key === asked) ? asked as ProgramWorkspace : LEGACY[asked] ?? 'overview';

  const load = useCallback(async () => {
    if (!Number.isSafeInteger(programId) || programId <= 0) { setProblem('This program link is not valid.'); setLoading(false); return; }
    setLoading(true);
    try {
      const [o, d] = await Promise.all([overviewApi.overview(programId), ffApi.program(programId)]);
      setOverview(o); setDetail(d); setProblem('');
    } catch (e) { setProblem(apiErrorMessage(e, 'The program could not be loaded.')); }
    finally { setLoading(false); }
  }, [programId]);
  useEffect(() => { void load(); }, [load]);

  // The workspace and what is selected in it live in the address, so a page can be linked, reloaded and read by the Copilot.
  const open = useCallback((workspace: ProgramWorkspace, query?: string | null) => {
    const next = new URLSearchParams();
    next.set('workspace', workspace);
    if (query) new URLSearchParams(query).forEach((value, key) => next.set(key, value));
    setSearchParams(next);
    window.scrollTo({top: 0});
  }, [setSearchParams]);

  const notify = useCallback((f: ActionFeedbackState | null) => setFeedback(f), []);
  const waiting = useMemo(() => {
    const by: Partial<Record<ProgramWorkspace, number>> = {};
    overview?.approvals.forEach(a => { by[a.workspace] = (by[a.workspace] ?? 0) + 1; });
    return by;
  }, [overview]);

  if (!overview || !detail) return <Surface>
    <PageHead><div><Link className="back" to={ABF_FORWARD_FLOW_PATH}><ArrowLeft size={14} /> Forward-flow programs</Link><h1>Forward flow</h1></div></PageHead>
    <Note role={problem ? 'alert' : 'status'}>{problem || (loading ? 'Loading the program…' : 'The program is not available.')}</Note>
  </Surface>;

  const {facts, nextAction} = overview;
  const gate = overview.purchaseGate;

  return <Surface>
    <FeedbackProvider value={notify}>
      <ContextHeader>
        <div className="top">
          <div>
            <Link className="back" to={ABF_FORWARD_FLOW_PATH}><ArrowLeft size={14} /> Forward-flow programs</Link>
            <h1>{overview.programName}</h1>
            <div className="meta">{overview.programCode} · {overview.portfolioName}<br />
              {facts.agreement.seller ? `Seller ${facts.agreement.seller} · servicer ${facts.agreement.servicer}` : 'No agreement in force'} · business date {day(overview.businessDate)}</div>
            <div className="chips">
              <StatusChip tone={word('program', overview.status).tone} label={overview.statusLabel} size="md" />
              <StatusChip tone={gate.open ? 'done' : 'warning'} label={gate.open ? 'Open for purchases' : 'Purchases on hold'} size="md" />
            </div>
          </div>
          <div className="facts" aria-label="Program headlines">
            <div className="fact"><span>Agreement in force</span><strong>{facts.agreement.versionNumber != null ? `Version ${facts.agreement.versionNumber}` : 'None'}</strong>
              <small>{facts.agreement.effectiveFrom ? `Effective ${day(facts.agreement.effectiveFrom)} · ${money(facts.agreement.pricePer100)} per $100` : 'Enter the agreement to start'}</small></div>
            <div className="fact" title={facts.capacity.reason}><span>Purchase limit remaining</span>
              <strong data-tone={facts.capacity.known ? 'emerald' : facts.capacity.closed ? undefined : 'amber'}>{facts.capacity.known ? money(facts.capacity.remaining, 0) : facts.capacity.closed ? 'Closed' : 'Not yet known'}</strong>
              <small>{facts.capacity.known ? `of ${money(facts.capacity.limit, 0)} · ${money(facts.capacity.monthlyRemaining, 0)} this month` : facts.capacity.reason}</small></div>
            <div className="fact"><span>Loans owned</span><strong>{Number(facts.owned.loans).toLocaleString('en-US')} · {money(facts.owned.upb, 0)}</strong>
              <small>Official book · balance</small></div>
            <div className="fact"><span>Owed to the seller</span><strong data-tone={Number(facts.owedToSeller.payable) > 0 ? 'amber' : undefined}>{money(facts.owedToSeller.payable)}</strong>
              <small>{Number(facts.owedToSeller.holdback) > 0 ? `${money(facts.owedToSeller.holdback)} holdback not yet due` : 'For loans booked, not yet paid'}</small></div>
            <div className="fact"><span>Offers in progress</span><strong>{facts.offers.open}</strong>
              <small>{gate.open && facts.offers.nextPurchaseDate ? `Next purchase day ${day(facts.offers.nextPurchaseDate)}` : `${facts.offers.total} received in all`}</small></div>
            <div className="fact"><span>Servicing</span><strong>{facts.servicing.periodLabel ?? 'No open period'}</strong>
              <small>{facts.servicing.nextStep ?? (facts.servicing.nextServicingDue ? `Next report due ${day(facts.servicing.nextServicingDue)}` : 'Starts with the first purchase')}</small></div>
            <div className="fact"><span>Waiting for a checker</span><strong data-tone={overview.approvals.length ? 'amber' : 'emerald'}>{overview.approvals.length}</strong>
              <small>{Number(facts.claims.open) > 0 ? `${count(Number(facts.claims.open), 'open seller claim')}` : 'No open seller claim'}</small></div>
            <div className="fact"><span>Next action</span><strong>{nextAction.label}</strong><small>{nextAction.whyNot ?? nextAction.reason}</small></div>
          </div>
        </div>
        <nav role="tablist" aria-label="Program workspaces">
          {TABS.map(([key, label]) => <button key={key} role="tab" aria-selected={tab === key} onClick={() => open(key)}>
            {label}{waiting[key] ? <i aria-label={`${waiting[key]} waiting for a checker`}>{waiting[key]}</i> : null}</button>)}
        </nav>
      </ContextHeader>

      <Body>
        {problem && <Note role="alert" style={{marginBottom: 14}}>{problem}</Note>}
        {tab === 'overview' && <ForwardFlowOverviewWorkspace overview={overview} loading={loading} onRefresh={() => void load()} onOpen={open} />}
        {tab === 'agreement' && <ForwardFlowAgreementWorkspace programId={programId} detail={detail}
          requestedVersion={Number(searchParams.get('version') ?? searchParams.get('reviewAgreement')) || null} onChanged={load} />}
        {tab === 'offers' && <ForwardFlowOffersWorkspace programId={programId} detail={detail} overview={overview}
          offerParam={searchParams.get('offer')} stepParam={searchParams.get('step')} onOpen={open} onChanged={load} />}
        {tab === 'assets' && <ForwardFlowAssetsWorkspace programId={programId} businessDate={detail.businessDate} />}
        {tab === 'collections' && <ForwardFlowCollectionsWorkspace programId={programId} businessDate={detail.businessDate} onChanged={() => void load()} />}
        {tab === 'monitoring' && <><ForwardFlowMonitoringWorkspace programId={programId} />
          <ForwardFlowRemediesPanel programId={programId} businessDate={detail.businessDate} onChanged={() => void load()} /></>}
        {tab === 'reporting' && <ForwardFlowReportingWorkspace programId={programId} />}
        {tab === 'controls' && <>
          <ForwardFlowGovernancePanel programId={programId} programStatus={detail.status} businessDate={detail.businessDate} onChanged={() => void load()} />
          <ForwardFlowAuthoritiesWorkbench programId={programId} />
          <ForwardFlowIdentityAliasPanel programId={programId} />
        </>}
        {tab === 'activity' && <ActivityLog programId={programId} refreshKey={overview.generatedAt} />}
      </Body>

      {tab !== 'overview' && <ForwardFlowApprovalsDock programId={programId} approvals={overview.approvals} onOpen={open}
        current={`${tab}?${searchParams.toString()}`} />}
      <ViewportToast feedback={feedback} onClose={() => setFeedback(null)} />
    </FeedbackProvider>
  </Surface>;
}

function ActivityLog({programId, refreshKey}: {programId: number; refreshKey: string}) {
  const [rows, setRows] = useState<Array<{id: number; action: string; actor: string; detail: unknown; happened_at: string}> | null>(null);
  const [problem, setProblem] = useState('');
  useEffect(() => { ffApi.activity(programId).then(setRows).catch(e => setProblem(apiErrorMessage(e, 'The activity could not be loaded.'))); }, [programId, refreshKey]);
  return <Section>
    <header><div><h2>Activity</h2><p>Who did what, and when, newest first. The last 200 entries.</p></div></header>
    {problem ? <Note role="alert">{problem}</Note> : rows === null ? <p>Loading…</p> : rows.length === 0 ? <p>Nothing has happened on this program yet.</p>
      : <Scroller><Grid><thead><tr><th>When</th><th>What happened</th><th>Who</th></tr></thead>
        <tbody>{rows.map(a => <tr key={a.id}><td style={{whiteSpace: 'nowrap'}}>{moment(a.happened_at)}</td><td>{activityWords(a.action)}</td>
          <td>{a.actor?.startsWith('SYSTEM') ? 'The system' : a.actor}</td></tr>)}</tbody></Grid></Scroller>}
  </Section>;
}

export {programRoute};
